import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Button,
  useTheme,
  TextField,
  Typography,
  CardContent,
  useMediaQuery,
} from "@mui/material";
import emailjs from "@emailjs/browser";
import { toast } from "react-toastify";

const styles = {
  InputStyling: {
    fontFamily: '"Poppins", serif',
    fontSize: { xs: 14, sm: 14, md: 16, lg: 16 },
    "& .MuiInputBase-input": {
      height: "50px",
      fontFamily: '"Poppins", serif',
      boxSizing: "border-box",
      fontSize: { xs: 14, sm: 14, md: 16, lg: 16 },
    },
  },
  InputLabelPropsStyling: {
    fontSize: { xs: 13, sm: 13, md: 14, lg: 14 },
  },
};

const ContactUsStyles = {
  privacyText: {
    fontSize: "0.8rem",
    color: "#777",
    textAlign: "center",
    marginTop: "1rem",
    fontFamily: '"Poppins", serif',
  },
  privacyLink: {
    color: "#0056E9",
    fontWeight: "bold",
    cursor: "pointer",
  },
};

function Form({ inline = false }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const nameRegex = /^[A-Za-z\s'-]+$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function validateField(field, value) {
    switch (field) {
      case "firstName":
        if (!value) return "First name is required";
        if (!nameRegex.test(value)) return "Only alphabetic characters";
        return "";
      case "lastName":
        if (!value) return "Last name is required";
        if (!nameRegex.test(value)) return "Only alphabetic characters";
        return "";
      case "email":
        if (!value) return "Email is required";
        if (!emailRegex.test(value)) return "Enter a valid email";
        return "";
      case "phone":
        if (!value) return "Phone is required";
        if (value.length !== 10) return "Phone must be 10 digits";
        return "";
      case "company":
        if (!value) return "Company name is required";
        return "";
      case "message":
        if (!value) return "Message is required";
        return "";
      default:
        return "";
    }
  }

  function validateAll({ silent = false } = {}) {
    const nextErrors = {};
    Object.entries(formValues).forEach(([field, val]) => {
      const message = validateField(field, val);
      if (message) nextErrors[field] = message;
    });
    if (!silent) setErrors(nextErrors);
    return Object.keys(nextErrors).length ? nextErrors : null;
  }

  const isSubmitDisabled = Boolean(validateAll({ silent: true }));

  function handleChange(field) {
    return (event) => {
      let value = event.target.value;
      if (field === "phone") {
        value = value.replace(/\D/g, "").slice(0, 10);
      }
      setFormValues((prev) => ({ ...prev, [field]: value }));
      const message = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: message }));
    };
  }

  function validate() {
    return !validateAll();
  }

  useEffect(() => {
    emailjs.init("PXhuBkpQsLGvZ7daX");
  }, []);

  async function handleButtonSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    const { firstName, lastName, email, phone, company, message } = formValues;

    const serviceId = "service_1dja0i8";
    const templateId = "template_ad8qksu";
    const templateParams = {
      name: `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      email,
      phone,
      company,
      message,
    };
    try {
      setLoading(true);
      await emailjs.send(serviceId, templateId, templateParams);
      toast.success("Message sent! We'll be in touch soon.");
      setFormValues({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });
      setErrors({});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast.error("There was an issue sending your message. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleButtonSubmit}
      sx={{
        position: "relative",
        bgcolor: "transparent",
        display: "flex",
        zIndex: "9",
        justifyContent: inline
          ? "flex-start"
          : { xs: "center", md: "flex-start" },
        width: inline ? "auto" : "100%",
      }}
    >
      <Card
        sx={{
          mb: { md: 0 },
          width: inline
            ? { xs: 320, sm: 360, md: "100%" }
            : { xs: "100%", sm: "90%", md: "100%" },
          boxShadow: 0,
          border: "1px solid gray",
          borderRadius: 3,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          p: 1,
        }}
      >
        <CardContent>
          <Box sx={{ marginTop: -2, marginBottom: -1 }}>
            <Typography variant="h6" sx={{ fontFamily: '"Poppins", serif' }}>
              Get In Touch With Us
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 0 }}>
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formValues.firstName}
              onChange={handleChange("firstName")}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
              sx={styles.InputStyling}
              InputLabelProps={{ sx: { fontFamily: '"Poppins", serif' } }}
              required
            />

            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formValues.lastName}
              onChange={handleChange("lastName")}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
              sx={styles.InputStyling}
              InputLabelProps={{ sx: { fontFamily: '"Poppins", serif' } }}
              required
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              variant="outlined"
              value={formValues.email}
              onChange={handleChange("email")}
              error={Boolean(errors.email)}
              helperText={errors.email}
              sx={styles.InputStyling}
              InputLabelProps={{ sx: { fontFamily: '"Poppins", serif' } }}
              required
            />

            <TextField
              fullWidth
              label="Phone"
              variant="outlined"
              value={formValues.phone}
              onChange={handleChange("phone")}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              sx={styles.InputStyling}
              InputLabelProps={{ sx: { fontFamily: '"Poppins", serif' } }}
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              required
            />
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              label="Company Name"
              variant="outlined"
              value={formValues.company}
              onChange={handleChange("company")}
              sx={styles.InputStyling}
              InputLabelProps={{ sx: { fontFamily: '"Poppins", serif' } }}
            />
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              label="Message"
              variant="outlined"
              multiline
              rows={3}
              value={formValues.message}
              onChange={handleChange("message")}
              sx={{
                fontFamily: '"Poppins", serif',
                fontSize: { xs: 14, sm: 14, md: 16, lg: 16 },
                "& .MuiInputBase-input": {
                  height: "50px",
                  fontFamily: '"Poppins", serif',
                  boxSizing: "border-box",
                  fontSize: { xs: 14, sm: 14, md: 16, lg: 16 },
                },
              }}
              InputLabelProps={{ sx: styles.InputLabelPropsStyling }}
            />
          </Box>

          <Box sx={ContactUsStyles.privacyText}>
            <Typography
              variant="body2"
              component="p"
              sx={{ fontFamily: '"Poppins", serif' }}
            >
              By submitting this form you agree to our
              <Box component="span" sx={{ mx: 0.5 }} />
              <Box component="span" sx={ContactUsStyles.privacyLink}>
                Privacy Policy
              </Box>
              . For more information on how your personal data will be
              processed, please read our privacy statement.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: -1.5 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled || loading}
              fullWidth={isSm}
              sx={{
                background: "linear-gradient(93.14deg, #1E90FF, #63B8FF)",
                color: "white",
                padding: "12px 24px",
                width: "100%",
                borderRadius: "8px",
                marginTop: "1rem",
                fontFamily: '"Poppins", serif',
                "&:hover": {
                  background: "linear-gradient(93.14deg, #63B8FF, #1E90FF)",
                },
              }}
            >
              {loading ? "Sending..." : "Submit"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Form;
